// Angular
import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';

// Local library
import {
  UnsubscribeObservableService,
  MatNotificationService
} from '@amwager/util';
import {
  CommonFacade,
  ENVIRONMENT,
  PermissionService,
  SessionService
} from '@amwager/data-access';
import {
  GUEST,
  IGetEventsDetailsResponse,
  IPool,
  IRace,
  isOnline$,
  LoggerService
} from '@amwager/util-ts';
import {
  ISelectedRace,
  RaceService,
  WagerService,
  WagerFacade
} from '@amwager/wager/data-access';
import { AccountFacade } from '@amwager/account/data-access';

// Angular Material Modules
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

// Third party
// import * as _ from 'lodash';
import _ from 'lodash';
import { take } from 'rxjs';

@Component({
  selector: 'feature-mat-race',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule
  ],
  providers: [DatePipe],
  templateUrl: './race.component.html',
  styleUrls: ['./race.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RaceComponent extends
UnsubscribeObservableService implements OnInit, OnChanges, AfterViewInit {
  @Input() selectedEvent!: IGetEventsDetailsResponse;

  @ViewChild('swipeRaceSectionTemplateRef') swipeRaceSectionTemplateRef!: ElementRef;

  selectedRace!: ISelectedRace;
  isShowRunnners = true;
  selectedPool!: IPool;
  pool!: string;
  modifier!: string;
  baseAmount!: number;
  setBaseAmount!: number;
  dblPkArray!: any;
  PoolIds: string[] = ['EX','QU','TRL','TRI','SPR','OMN','PEN','HEX'];

  @Input() selectedModifier!: string;
  @Input() currentSelectedFromEventStatus?: string | null;

  isExoticSelections!: boolean;
  modal: any;

  runId!: string;
  eventId!: string;
  raceId!: string;

  liveVideoUrl!: string;
  playLiveVideo = false;
  @Input() triggerLiveVideo!: boolean;
  @Output() doCloseVideoFn = new EventEmitter<boolean>();
  @Output() doSetRaceStatusFn = new EventEmitter<number>();
  // Emits { pool, selectedRace, dblPkArray } on race click; reuses doGetPool handler.
  @Output() doGetRaceFn = new EventEmitter<any>();

  private previousSelectedEventID!: string;
  private previousSelectedRaceID!: string;

  isOnline: boolean = true;

  constructor(
    readonly cdRef: ChangeDetectorRef,
    readonly permissionService: PermissionService,
    private readonly sessionService: SessionService,
    readonly router: Router,
    private readonly elementRef: ElementRef,
    readonly wagerFacade: WagerFacade,
    private readonly accountFacade: AccountFacade,
    private readonly commonFacade: CommonFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly raceService: RaceService,
    private readonly wagerService: WagerService,
    private readonly loggerService: LoggerService,
    @Inject(ENVIRONMENT) readonly environment: string,
  ) {
    super();

    this.observerGetSelectedRace();

    this.wagerFacade.toUpdateGeneralEvents$().subscribe((toUpdate) => {
      if (!toUpdate) return;

      this.wagerFacade.getSelectedRace$()
        .pipe(take(1))
        .subscribe((race?: ISelectedRace) => {
          if (!race || !race?.RaceId || !race?.runId || !race?.eventId) return;

          if (
            this.raceId === String(race.RaceId)
            && this.runId === String(race.runId)
            && this.eventId === String(race.eventId)
          ) return;

          this.selectedRace = race;
        });
    });

  }

  /**
   * Init on load
   * @returns {void}
   */
  ngOnInit(): void {
    this.wagerService.checkConnectivity(
      isOnline$, this.environment
    ).subscribe((online: any) => {
      this.isOnline = online;
      this.commonFacade.setIsConnectedInternet('WagerComponent:checkConnectivity', online);
    });
  }

  /**
   * Init after view loaded
   * @param {any} changes listen to data changes
   * @returns {void}
   */
  ngOnChanges(changes: SimpleChanges): void {

    if (
      changes['selectedEvent'] !== undefined &&
      changes['selectedEvent'].currentValue !== undefined
    ) {

      if (changes['selectedEvent'].currentValue.id) {
        // triggered live video
        if (this.playLiveVideo) return;
      }

      // Scroll target is the newly selected track's current race, not the
      // stale previously-selected race (which never centered the new track).
      const newCurrentRaceId =
        changes['selectedEvent'].currentValue?.ToteResponse?.EventDetail?.currentRace?.RaceId
        ?? this.selectedRace?.RaceId
        ?? 0;

      // proceed if selected event ID is not the same from Parent input
      // skip if selected event ID is the same from Parent input
      if (
        // if different event ID
        changes['selectedEvent'].currentValue.id !== this.previousSelectedEventID
      ) {
        this.previousSelectedEventID = changes['selectedEvent'].currentValue.id;
        // OLD: scrolled to this.selectedRace?.RaceId (stale on track change)
        // this.raceService.setDefaultValues(
        //   this.selectedRace?.RaceId || 0,
        //   this.swipeRaceSectionTemplateRef,
        //   this.doSetRaceStatusFn
        // );
        this.raceService.setDefaultValues(
          newCurrentRaceId,
          this.swipeRaceSectionTemplateRef,
          this.doSetRaceStatusFn
        );

      } else this.loggerService.log('RACE:onchanges:RACE:ERROR',);

      if (
        changes['selectedEvent'].currentValue?.ToteResponse?.EventDetail?.currentRace?.RaceId
        !== this.previousSelectedRaceID
      ) {
        this.previousSelectedRaceID
          = changes['selectedEvent'].currentValue?.ToteResponse?.EventDetail?.currentRace?.RaceId;
        // OLD: scrolled to this.selectedRace?.RaceId (stale on track change)
        // this.raceService.setDefaultValues(
        //   this.selectedRace?.RaceId || 0,
        //   this.swipeRaceSectionTemplateRef,
        //   this.doSetRaceStatusFn
        // );
        this.raceService.setDefaultValues(
          newCurrentRaceId,
          this.swipeRaceSectionTemplateRef,
          this.doSetRaceStatusFn
        );

      }

    }

    this.cdRef.markForCheck();
  }

  /**
   * Init after view loaded
   * @returns {void}
   */
  ngAfterViewInit(): void {
    // null;
    // On full page load ngOnChanges can fire before this view exists, so the
    // pagination never scrolls. Center on the current race once it's ready.
    setTimeout(() => {
      const initialRaceId =
        this.selectedEvent?.ToteResponse?.EventDetail?.currentRace?.RaceId
        ?? this.selectedRace?.RaceId
        ?? 0;
      this.raceService.scrollToPage(initialRaceId, this.swipeRaceSectionTemplateRef);
    }, 100);
  }

  /**
   * Observers
   * Fetch current race
   * @returns {void}
   */
  observerGetSelectedRace(): void {
    this.wagerFacade.getSelectedRace$()
      .subscribe((race?: ISelectedRace) => {
        if (!race || !race?.RaceId || !race?.runId || !race?.eventId) return;


        this.selectedRace = race;

        // encase empty entries
        const updatedRunners = this.raceService.processFillInRunnersNonDblPk(
          this.selectedRace.Runners
        );
        const updatedRace = _.cloneDeep(this.selectedRace);
        updatedRace.Runners = updatedRunners;
        this.selectedRace = updatedRace;

        // avoid proccess for the same race #
        if (
          this.raceId === String(race.RaceId)
          && this.runId === String(race.runId)
          && this.eventId === String(race.eventId)
        ) null;
        else {
          // clear matrix every new Race ID
          this.wagerFacade.clearRaceAllOddsMatrixes('RaceComponent:observerGetSelectedRace');
          this.wagerFacade.clearSelectedRaceMatrix('RaceComponent:observerGetSelectedRace');
          this.wagerFacade.clearSelectedRacePoolsTotals('RaceComponent:observerGetSelectedRace');
          this.wagerFacade.clearSelectedRaceWillPays('RaceComponent:observerGetSelectedRace');

          this.raceId = String(race.RaceId);
          this.runId = String(race.runId);
          this.eventId = String(race.eventId);

          // build matrix tables
          this.toBuildMatrixTables({
            ...race as IRace,
            runId: this.runId,
            eventId: this.eventId
          });
        }

        this.cdRef.markForCheck();
      });
  }

  /**
   * Set event race
   * @param {any} $event current DOM element
   * @param {IRace} race current Race
   * @returns {void}
   */
  selectRace($event: any, race: IRace): void {
    // clear active legs/round
    // this.raceService.clearLegRoundSelector(this.elementRef);
    this.isShowRunnners = false;
    this.selectedRace = race;
    this.selectedPool = (this.selectedRace as IRace)?.Pools?.[0];
    this.selectedModifier = '';
    this.dblPkArray = '';

    this.pool = this.selectedPool?.PoolName || 'WIN';
    this.modifier = 'Straight';

    this.wagerFacade.selectedRace(
      'RaceComponent:selectRace',
      {
        ...race,
        runId: this.selectedEvent?.ToteResponse?.RunId,
        eventId: this.selectedEvent?.ToteResponse?.EventDetail?.EventId
      }
    );

    // Finished race → jump to results page with this event/race
    // pre-selected. Store push above ensures the matrix / bets /
    // event dropdown all resolve to the same selection on arrival.
    if (race?.RaceStatus === 3) {
      // Results are a real-user feature; send guests to login
      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.router.navigate(['/login']);
        return;
      }

      this.router.navigate(['/results'], {
        state: {
          eventId: this.selectedEvent?.id,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          raceId: race?.RaceId,
        }
      });
      return;
    }

    // $event?.target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    this.doSetRaceStatusFn.emit(this.selectedRace?.RaceStatus);

    this.raceService.scrollToPage(this.selectedRace?.RaceId || 0, this.swipeRaceSectionTemplateRef);

    this.cdRef.markForCheck(); // re-render for template

    setTimeout(() => {
      this.isShowRunnners = true;
      this.cdRef.markForCheck(); // re-render for template
    }, 100);
  }

  /**
   * Build matrix tables
   * @param {IRace & {runId: string, eventId: string}} race current Race
   * @returns {void}
   */
  toBuildMatrixTables(race: IRace & {runId: string, eventId: string}): void {
    this.wagerFacade.updateRaceMatrix('MatrixComponent:construct', undefined);
    this.wagerFacade.updateRacePoolsTotals('MatrixComponent:construct', undefined);

    if (!race || !race?.RaceId) return;

    const raceId = race?.RaceId;
    const eventId = race?.eventId;
    const runId = race?.runId;

    if (!raceId || !eventId || !runId) return;

    const poolList = [
      'TRU',
      ...race?.PoolList?.split(",") || [],
    ];

    // get pool matrixes
    _.forEach(poolList, (poolId: string) => {
      if (!poolId) return;

      this.wagerFacade.getPoolMatrix(
        'RaceComponent:toBuildMatrixTables',
        {
          raceId: raceId,
          eventId: eventId,
          runId: runId,
          poolId
        }
      );
    });

    // get pools totals
    this.wagerFacade.getPoolsTotals(
      'RaceComponent:toBuildMatrixTables',
      {
        raceId: raceId,
        eventId: eventId,
        runId: runId,
      }
    );

    // get will pays
    this.wagerFacade.getWillPays(
      'RaceComponent:toBuildMatrixTables',
      {
        raceId: raceId,
        eventId: eventId,
        runId: runId,
      }
    );

    // get race odd matrixes with WIN pool
    this.wagerFacade.getRaceAllOddsMatrixes(
      'RaceComponent:toBuildMatrixTables',
      'WIN',
      {
        raceId: raceId,
        eventId: eventId,
        runId: runId,
        poolId: 'WIN'
      }
    );

    // get race odd matrixes with TRU pool
    this.wagerFacade.getRaceAllOddsMatrixes(
      'RaceComponent:toBuildMatrixTables',
      'TRU',
      {
        raceId: raceId,
        eventId: eventId,
        runId: runId,
        poolId: 'TRU'
      }
    );
  }

}
